import React, {useEffect, useMemo, useState} from "react";
import axios from "axios";
import Swal from "sweetalert2";
import {DataGrid, GridToolbar} from "@mui/x-data-grid";
import {
    Box,
    Button,
    Card,
    CardContent,
    Chip,
    CircularProgress,
    Grid,
    IconButton,
    MenuItem,
    TextField,
    Typography
} from "@mui/material";
import {Add, CalendarMonth, Clear, Edit, EventAvailable, Search} from "@mui/icons-material";
import {baseUrl, ipAddress, userLocation, userName} from "../Univ.js";
import "./VaccinationStyles.css";
const VaccinationAppointments = () => {
    /* ============================================================
       TAB
       ============================================================ */
    const [activeTab, setActiveTab] = useState("appointments");
    /* ============================================================
       LOADING
       ============================================================ */
    const [loading, setLoading] = useState(false);
    const [patientSearchLoading, setPatientSearchLoading] = useState(false);
    /* ============================================================
       MASTER DATA
       ============================================================ */
    const [vaccines, setVaccines] = useState([]);
    const [schedules, setSchedules] = useState([]);
    const [scheduleDoses, setScheduleDoses] = useState([]);
    /* ============================================================
       PATIENT SEARCH
       ============================================================ */
    const [patientSearch, setPatientSearch] = useState("");
    const [patientResults, setPatientResults] = useState([]);
    const [selectedPatient, setSelectedPatient] = useState(null);
    /* ============================================================
       APPOINTMENTS
       ============================================================ */
    const [appointments, setAppointments] = useState([]);
    const [appointmentLoading, setAppointmentLoading] = useState(false);
    /* ============================================================
       FORM
       ============================================================ */
    const emptyForm = {
        patient: "",
        schedule: "",
        schedule_dose: "",
        vaccine: "",
        dose_number: 1,
        dose_name: "",
        scheduled_date: "",
        scheduled_time: "",
        status: "SCHEDULED",
        appointment_type: "ROUTINE",
        vaccination_site: "",
        route: "",
        remarks: "",
        addedby: userName,
        ip_add: ipAddress,
        location: userLocation
    };
    const [formData, setFormData] = useState(emptyForm);
    const [editingId, setEditingId] = useState(null);
    /* ============================================================
       LOAD MASTER DATA
       ============================================================ */
    useEffect(() => {
        loadMasterData();
    }, []);
    const loadMasterData = async () => {
        try {
            setLoading(true);
            const [vaccineResponse, scheduleResponse, doseResponse] = await Promise.all([axios.get(`${baseUrl}vaccination/vaccines/`), axios.get(`${baseUrl}vaccination/schedules/`), axios.get(`${baseUrl}vaccination/schedule-doses/`)]);
            setVaccines(Array.isArray(vaccineResponse.data) ? vaccineResponse.data : vaccineResponse.data.results || []);
            setSchedules(Array.isArray(scheduleResponse.data) ? scheduleResponse.data : scheduleResponse.data.results || []);
            setScheduleDoses(Array.isArray(doseResponse.data) ? doseResponse.data : doseResponse.data.results || []);
        } catch (error) {
            console.error("Vaccination master loading error:", error);
            Swal.fire("Error", "Unable to load vaccination master data.", "error");
        } finally {
            setLoading(false);
        }
    };
    /* ============================================================
       PATIENT SEARCH
       ============================================================ */
    const searchPatients = async (value) => {
        setPatientSearch(value);
        if (!value || value.trim().length < 2) {
            setPatientResults([]);
            return;
        }
        try {
            setPatientSearchLoading(true);
            const response = await axios.get(`${baseUrl}pat-search/`, {
                params: {
                    search: value
                }
            });
            const data = Array.isArray(response.data) ? response.data : response.data.results || [];
            setPatientResults(data);
        } catch (error) {
            console.error("Patient search error:", error);
            setPatientResults([]);
        } finally {
            setPatientSearchLoading(false);
        }
    };
    /* ============================================================
       SELECT PATIENT
       ============================================================ */
    const selectPatient = (patient) => {
        setSelectedPatient(patient);
        setPatientSearch(`${patient.patient || ""} - ${patient.pat_id || ""}`);
        setPatientResults([]);
        setFormData(prev => ({
            ...prev, patient: patient.id
        }));
        loadAppointments(patient.id);
    };
    /* ============================================================
       CLEAR PATIENT
       ============================================================ */
    const clearPatient = () => {
        setSelectedPatient(null);
        setPatientSearch("");
        setPatientResults([]);
        setAppointments([]);
        resetForm();
    };
    /* ============================================================
       LOAD PATIENT APPOINTMENTS
       ============================================================ */
    const loadAppointments = async (patientId) => {
        if (!patientId) {
            setAppointments([]);
            return;
        }
        try {
            setAppointmentLoading(true);
            const response = await axios.get(`${baseUrl}vaccination/appointments/`, {
                params: {
                    patient: patientId
                }
            });
            const data = Array.isArray(response.data) ? response.data : response.data.results || [];
            setAppointments(data);
        } catch (error) {
            console.error("Appointment loading error:", error);
            setAppointments([]);
            Swal.fire("Error", "Unable to load vaccination appointments.", "error");
        } finally {
            setAppointmentLoading(false);
        }
    };
    /* ============================================================
       SCHEDULE DOSE FILTER
       ============================================================ */
    /* ============================================================
   SCHEDULE DOSE FILTER
   ============================================================ */
    const filteredDoses = useMemo(() => {
        if (!formData.schedule) {
            return [];
        }
        return scheduleDoses.filter(dose => {
            let scheduleId = dose.schedule;
            if (scheduleId && typeof scheduleId === "object") {
                scheduleId = scheduleId.id;
            }
            return String(scheduleId) === String(formData.schedule);
        });
    }, [scheduleDoses, formData.schedule]);
    /* ============================================================
       FORM CHANGE
       ============================================================ */
    /* ============================================================
   FORM CHANGE
   ============================================================ */
    const handleChange = (event) => {
        const {
            name, value
        } = event.target;
        /* --------------------------------------------------------
           SCHEDULE
           -------------------------------------------------------- */
        if (name === "schedule") {
            setFormData(prev => ({
                ...prev, schedule: value, schedule_dose: "", vaccine: "", dose_number: 1, dose_name: "", route: ""
            }));
            return;
        }
        /* --------------------------------------------------------
           SCHEDULE DOSE
           -------------------------------------------------------- */
        if (name === "schedule_dose") {
            const selectedDose = scheduleDoses.find(dose => String(dose.id) === String(value));
            if (!selectedDose) {
                setFormData(prev => ({
                    ...prev, schedule_dose: value
                }));
                return;
            }
            /* ----------------------------------------------------
               GET VACCINE ID
               ---------------------------------------------------- */
            let vaccineId = "";
            if (selectedDose.vaccine && typeof selectedDose.vaccine === "object") {
                vaccineId = selectedDose.vaccine.id || "";
            } else {
                vaccineId = selectedDose.vaccine || "";
            }
            /* ----------------------------------------------------
               FIND VACCINE MASTER
               ---------------------------------------------------- */
            const selectedVaccine = vaccines.find(vaccine => String(vaccine.id) === String(vaccineId));
            /* ----------------------------------------------------
               ROUTE
               ---------------------------------------------------- */
            let route = "";
            if (selectedVaccine) {
                route = selectedVaccine.route || selectedVaccine.vaccination_route || "";
            }
            /* ----------------------------------------------------
               FALLBACK:
               VACCINE DATA MAY ALREADY BE INSIDE DOSE
               ---------------------------------------------------- */
            if (!route) {
                route = selectedDose.vaccine_detail?.route || selectedDose.vaccine_data?.route || selectedDose.vaccine?.route || "";
            }
            /* ----------------------------------------------------
               UPDATE FORM
               ---------------------------------------------------- */
            setFormData(prev => ({
                ...prev,
                schedule_dose: selectedDose.id,
                vaccine: vaccineId,
                dose_number: selectedDose.dose_number || 1,
                dose_name: selectedDose.dose_name || "",
                route: route
            }));
            return;
        }
        /* --------------------------------------------------------
           VACCINE MANUAL CHANGE
           -------------------------------------------------------- */
        if (name === "vaccine") {
            const selectedVaccine = vaccines.find(vaccine => String(vaccine.id) === String(value));
            setFormData(prev => ({
                ...prev, vaccine: value, route: selectedVaccine?.route || selectedVaccine?.vaccination_route || ""
            }));
            return;
        }
        /* --------------------------------------------------------
           NORMAL FIELD
           -------------------------------------------------------- */
        setFormData(prev => ({
            ...prev, [name]: value
        }));
    };
    /* ============================================================
       SAVE APPOINTMENT
       ============================================================ */
    const saveAppointment = async (event) => {
        event.preventDefault();
        if (!selectedPatient) {
            Swal.fire("Patient Required", "Please select a patient.", "warning");
            return;
        }
        if (!formData.schedule) {
            Swal.fire("Schedule Required", "Please select a vaccination schedule.", "warning");
            return;
        }
        if (!formData.schedule_dose) {
            Swal.fire("Dose Required", "Please select a vaccination dose.", "warning");
            return;
        }
        if (!formData.vaccine) {
            Swal.fire("Vaccine Required", "Please select a vaccine.", "warning");
            return;
        }
        if (!formData.scheduled_date) {
            Swal.fire("Date Required", "Please select the vaccination date.", "warning");
            return;
        }
        try {
            setLoading(true);
            const payload = {
                ...formData, patient: selectedPatient.id, addedby: userName, ip_add: ipAddress, location: userLocation
            };
            if (editingId) {
                await axios.put(`${baseUrl}vaccination/appointments/${editingId}/`, payload);
            } else {
                await axios.post(`${baseUrl}vaccination/appointments/`, payload);
            }
            Swal.fire("Success", editingId ? "Vaccination appointment updated successfully." : "Vaccination appointment scheduled successfully.", "success");
            await loadAppointments(selectedPatient.id);
            resetForm();
        } catch (error) {
            console.error("Appointment save error:", error);
            let message = "Unable to save vaccination appointment.";
            if (error.response && error.response.data) {
                message = typeof error.response.data === "string" ? error.response.data : JSON.stringify(error.response.data);
            }
            Swal.fire("Error", message, "error");
        } finally {
            setLoading(false);
        }
    };
    /* ============================================================
       EDIT APPOINTMENT
       ============================================================ */
    const editAppointment = (row) => {
        setEditingId(row.id);
        setFormData({
            patient: row.patient,
            schedule: row.schedule || "",
            schedule_dose: row.schedule_dose || "",
            vaccine: row.vaccine || "",
            dose_number: row.dose_number || 1,
            dose_name: row.dose_name || "",
            scheduled_date: row.scheduled_date || "",
            scheduled_time: row.scheduled_time || "",
            status: row.status || "SCHEDULED",
            appointment_type: row.appointment_type || "ROUTINE",
            vaccination_site: row.vaccination_site || "",
            route: row.route || "",
            remarks: row.remarks || "",
            addedby: row.addedby || userName,
            ip_add: row.ip_add || ipAddress,
            location: row.location || userLocation
        });
        setActiveTab("appointments");
        window.scrollTo({
            top: 0, behavior: "smooth"
        });
    };
    /* ============================================================
       CANCEL APPOINTMENT
       ============================================================ */
    const cancelAppointment = async (row) => {
        const result = await Swal.fire({
            title: "Cancel Vaccination Appointment?",
            text: "This appointment will be marked as cancelled.",
            icon: "warning",
            showCancelButton: true,
            confirmButtonText: "Yes, Cancel",
            cancelButtonText: "No"
        });
        if (!result.isConfirmed) {
            return;
        }
        try {
            await axios.patch(`${baseUrl}vaccination/appointments/${row.id}/`, {
                status: "CANCELLED", ip_add: ipAddress, location: userLocation
            });
            Swal.fire("Cancelled", "Vaccination appointment cancelled.", "success");
            if (selectedPatient) {
                loadAppointments(selectedPatient.id);
            }
        } catch (error) {
            console.error("Cancel appointment error:", error);
            Swal.fire("Error", "Unable to cancel appointment.", "error");
        }
    };
    /* ============================================================
       RESET FORM
       ============================================================ */
    const resetForm = () => {
        setEditingId(null);
        setFormData({
            ...emptyForm, patient: selectedPatient ? selectedPatient.id : ""
        });
    };
    /* ============================================================
       STATUS CHIP
       ============================================================ */
    const statusChip = (status) => {
        let color = "default";
        switch (status) {
            case "SCHEDULED":
                color = "primary";
                break;
            case "CONFIRMED":
                color = "success";
                break;
            case "COMPLETED":
                color = "success";
                break;
            case "CANCELLED":
                color = "error";
                break;
            case "MISSED":
                color = "warning";
                break;
            case "OVERDUE":
                color = "error";
                break;
            default:
                color = "default";
        }
        return (<Chip
            label={status || "UNKNOWN"}
            color={color}
            size="small"
        />);
    };
    /* ============================================================
       GRID COLUMNS
       ============================================================ */
    const columns = [{
        field: "id", headerName: "ID", width: 70
    }, {
        field: "scheduled_date", headerName: "Date", width: 120
    }, {
        field: "scheduled_time", headerName: "Time", width: 100
    }, {
        field: "vaccine", headerName: "Vaccine", width: 180, renderCell: (params) => {
            if (params.row.vaccine_detail) {
                return (params.row.vaccine_detail.vaccine_name || "");
            }
            if (params.row.vaccine_data) {
                return (params.row.vaccine_data.vaccine_name || "");
            }
            if (typeof params.row.vaccine === "object") {
                return (params.row.vaccine.vaccine_name || "");
            }
            const vaccine = vaccines.find(item => String(item.id) === String(params.row.vaccine));
            return vaccine ? vaccine.vaccine_name : "";
        }
    }, {
        field: "dose_number", headerName: "Dose", width: 90
    }, {
        field: "dose_name", headerName: "Dose Name", width: 140
    }, {
        field: "appointment_type", headerName: "Type", width: 120
    }, {
        field: "status", headerName: "Status", width: 130, renderCell: (params) => statusChip(params.value)
    }, {
        field: "vaccination_site", headerName: "Site", width: 150
    }, {
        field: "remarks", headerName: "Remarks", width: 220
    }, {
        field: "actions", headerName: "Actions", width: 130, sortable: false, renderCell: (params) => (<Box
            sx={{
                display: "flex", gap: 0.5
            }}
        >

            <IconButton
                size="small"
                color="primary"
                onClick={() => editAppointment(params.row)}
            >
                <Edit fontSize="small"/>
            </IconButton>


            {params.row.status !== "CANCELLED" && params.row.status !== "COMPLETED" && (<IconButton
                size="small"
                color="error"
                onClick={() => cancelAppointment(params.row)}
            >
                <Clear fontSize="small"/>
            </IconButton>)}

        </Box>)
    }];
    /* ============================================================
       UPCOMING COUNT
       ============================================================ */
    const upcomingCount = appointments.filter(item => item.status === "SCHEDULED" || item.status === "CONFIRMED").length;
    const completedCount = appointments.filter(item => item.status === "COMPLETED").length;
    const cancelledCount = appointments.filter(item => item.status === "CANCELLED").length;
    /* ============================================================
       RENDER
       ============================================================ */
    return (<div className="vaccination-module">

        {/* ====================================================
               HEADER
               ==================================================== */}

        <div className="vaccination-page-header">

            <div>

                <Typography
                    variant="h5"
                    className="vaccination-page-title"
                >
                    Vaccination Scheduling
                </Typography>

                <Typography
                    variant="body2"
                    className="vaccination-page-subtitle"
                >
                    Schedule and manage patient vaccination
                    appointments
                </Typography>

            </div>


            <div className="vaccination-header-icon">

                <CalendarMonth/>

            </div>

        </div>


        {/* ====================================================
               SUMMARY CARDS
               ==================================================== */}

        {selectedPatient && (<Grid
            container
            spacing={2}
            className="vaccination-summary-row"
        >

            <Grid item xs={12} md={4}>

                <Card className="vaccination-summary-card">

                    <CardContent>

                        <Typography
                            variant="body2"
                        >
                            Upcoming
                        </Typography>

                        <Typography
                            variant="h4"
                        >
                            {upcomingCount}
                        </Typography>

                    </CardContent>

                </Card>

            </Grid>


            <Grid item xs={12} md={4}>

                <Card className="vaccination-summary-card">

                    <CardContent>

                        <Typography
                            variant="body2"
                        >
                            Completed
                        </Typography>

                        <Typography
                            variant="h4"
                        >
                            {completedCount}
                        </Typography>

                    </CardContent>

                </Card>

            </Grid>


            <Grid item xs={12} md={4}>

                <Card className="vaccination-summary-card">

                    <CardContent>

                        <Typography
                            variant="body2"
                        >
                            Cancelled
                        </Typography>

                        <Typography
                            variant="h4"
                        >
                            {cancelledCount}
                        </Typography>

                    </CardContent>

                </Card>

            </Grid>

        </Grid>)}


        {/* ====================================================
               PATIENT SEARCH
               ==================================================== */}

        <Card className="vaccination-section-card">

            <CardContent>

                <div className="vaccination-section-title">

                    <Search/>

                    <Typography variant="h6">
                        Select Patient
                    </Typography>

                </div>


                <div className="vaccination-patient-search">

                    <TextField
                        fullWidth
                        label="Search Patient"
                        placeholder="Patient name / Patient ID / Mobile"
                        value={patientSearch}
                        onChange={(event) => searchPatients(event.target.value)}
                    />


                    {patientSearchLoading && (<CircularProgress
                        size={22}
                        className="vaccination-search-loader"
                    />)}


                    {patientResults.length > 0 && (<div className="vaccination-patient-results">

                        {patientResults.map(patient => (<div
                            key={patient.id}
                            className="vaccination-patient-result"
                            onClick={() => selectPatient(patient)}
                        >

                            <strong>
                                {patient.patient}
                            </strong>

                            <span>
                                                ID: {patient.pat_id}
                                            </span>

                            {patient.mobile && (<span>
                                                    Mobile: {patient.mobile}
                                                </span>)}

                        </div>))}

                    </div>)}

                </div>


                {selectedPatient && (<div className="vaccination-selected-patient">

                    <div>

                        <strong>
                            {selectedPatient.patient}
                        </strong>

                        <span>
                                    Patient ID: {selectedPatient.pat_id}
                                </span>

                        {selectedPatient.gender && (<span>
                                        Gender: {selectedPatient.gender}
                                    </span>)}

                        {selectedPatient.age && (<span>
                                        Age: {selectedPatient.age}
                                    </span>)}

                        {selectedPatient.mobile && (<span>
                                        Mobile: {selectedPatient.mobile}
                                    </span>)}

                    </div>


                    <Button
                        variant="outlined"
                        color="error"
                        startIcon={<Clear/>}
                        onClick={clearPatient}
                    >
                        Clear Patient
                    </Button>

                </div>)}

            </CardContent>

        </Card>


        {/* ====================================================
               APPOINTMENT FORM
               ==================================================== */}

        {selectedPatient && (<Card className="vaccination-section-card">

            <CardContent>

                <div className="vaccination-section-title">

                    <EventAvailable/>

                    <Typography variant="h6">

                        {editingId ? "Edit Vaccination Appointment" : "Schedule Vaccination"}

                    </Typography>

                </div>


                <form
                    onSubmit={saveAppointment}
                >

                    <Grid
                        container
                        spacing={2}
                    >

                        {/* --------------------------------
                                   SCHEDULE
                                   -------------------------------- */}

                        <Grid
                            item
                            xs={12}
                            md={4}
                        >

                            <TextField
                                select
                                fullWidth
                                label="Vaccination Schedule"
                                name="schedule"
                                value={formData.schedule}
                                onChange={handleChange}
                            >

                                <MenuItem value="">
                                    Select Schedule
                                </MenuItem>

                                {schedules.map(schedule => (<MenuItem
                                    key={schedule.id}
                                    value={schedule.id}
                                >
                                    {schedule.schedule_name}
                                </MenuItem>))}

                            </TextField>

                        </Grid>


                        {/* --------------------------------
                                   DOSE
                                   -------------------------------- */}

                        <Grid
                            item
                            xs={12}
                            md={4}
                        >

                            <TextField
                                select
                                fullWidth
                                label="Schedule Dose"
                                name="schedule_dose"
                                value={formData.schedule_dose}
                                onChange={handleChange}
                                disabled={!formData.schedule}
                            >

                                <MenuItem value="">
                                    Select Dose
                                </MenuItem>

                                {filteredDoses.map(dose => (<MenuItem
                                    key={dose.id}
                                    value={dose.id}
                                >

                                    {dose.dose_name ? `Dose ${dose.dose_number} - ${dose.dose_name}` : `Dose ${dose.dose_number}`}

                                </MenuItem>))}

                            </TextField>

                        </Grid>


                        {/* --------------------------------
                                   VACCINE
                                   -------------------------------- */}

                        <Grid
                            item
                            xs={12}
                            md={4}
                        >

                            <TextField
                                select
                                fullWidth
                                label="Vaccine"
                                name="vaccine"
                                value={formData.vaccine}
                                onChange={handleChange}
                            >

                                <MenuItem value="">
                                    Select Vaccine
                                </MenuItem>

                                {vaccines.map(vaccine => (<MenuItem
                                    key={vaccine.id}
                                    value={vaccine.id}
                                >
                                    {vaccine.vaccine_name}
                                </MenuItem>))}

                            </TextField>

                        </Grid>


                        {/* --------------------------------
                                   DOSE NUMBER
                                   -------------------------------- */}

                        <Grid
                            item
                            xs={12}
                            md={3}
                        >

                            <TextField
                                fullWidth
                                label="Dose Number"
                                name="dose_number"
                                type="number"
                                value={formData.dose_number}
                                onChange={handleChange}
                            />

                        </Grid>


                        {/* --------------------------------
                                   DOSE NAME
                                   -------------------------------- */}

                        <Grid
                            item
                            xs={12}
                            md={3}
                        >

                            <TextField
                                fullWidth
                                label="Dose Name"
                                name="dose_name"
                                value={formData.dose_name}
                                onChange={handleChange}
                            />

                        </Grid>


                        {/* --------------------------------
                                   DATE
                                   -------------------------------- */}

                        <Grid
                            item
                            xs={12}
                            md={3}
                        >

                            <TextField
                                fullWidth
                                label="Scheduled Date"
                                name="scheduled_date"
                                type="date"
                                value={formData.scheduled_date}
                                onChange={handleChange}
                                InputLabelProps={{
                                    shrink: true
                                }}
                            />

                        </Grid>


                        {/* --------------------------------
                                   TIME
                                   -------------------------------- */}

                        <Grid
                            item
                            xs={12}
                            md={3}
                        >

                            <TextField
                                fullWidth
                                label="Scheduled Time"
                                name="scheduled_time"
                                type="time"
                                value={formData.scheduled_time}
                                onChange={handleChange}
                                InputLabelProps={{
                                    shrink: true
                                }}
                            />

                        </Grid>


                        {/* --------------------------------
                                   STATUS
                                   -------------------------------- */}

                        <Grid
                            item
                            xs={12}
                            md={3}
                        >

                            <TextField
                                select
                                fullWidth
                                label="Status"
                                name="status"
                                value={formData.status}
                                onChange={handleChange}
                            >

                                <MenuItem value="SCHEDULED">
                                    Scheduled
                                </MenuItem>

                                <MenuItem value="CONFIRMED">
                                    Confirmed
                                </MenuItem>

                                <MenuItem value="MISSED">
                                    Missed
                                </MenuItem>

                                <MenuItem value="CANCELLED">
                                    Cancelled
                                </MenuItem>

                            </TextField>

                        </Grid>


                        {/* --------------------------------
                                   APPOINTMENT TYPE
                                   -------------------------------- */}

                        <Grid
                            item
                            xs={12}
                            md={3}
                        >

                            <TextField
                                select
                                fullWidth
                                label="Appointment Type"
                                name="appointment_type"
                                value={formData.appointment_type}
                                onChange={handleChange}
                            >

                                <MenuItem value="ROUTINE">
                                    Routine
                                </MenuItem>

                                <MenuItem value="FOLLOW_UP">
                                    Follow Up
                                </MenuItem>

                                <MenuItem value="BOOSTER">
                                    Booster
                                </MenuItem>

                                <MenuItem value="CATCH_UP">
                                    Catch Up
                                </MenuItem>

                            </TextField>

                        </Grid>


                        {/* --------------------------------
                                   SITE
                                   -------------------------------- */}

                        <Grid
                            item
                            xs={12}
                            md={3}
                        >

                            <TextField
                                fullWidth
                                label="Vaccination Site"
                                name="vaccination_site"
                                value={formData.vaccination_site}
                                onChange={handleChange}
                            />

                        </Grid>


                        {/* --------------------------------
                                   ROUTE
                                   -------------------------------- */}

                        <Grid
                            item
                            xs={12}
                            md={3}
                        >

                            <TextField
                                fullWidth
                                label="Route"
                                name="route"
                                value={formData.route}
                                onChange={handleChange}
                            />

                        </Grid>


                        {/* --------------------------------
                                   REMARKS
                                   -------------------------------- */}

                        <Grid
                            item
                            xs={12}
                            md={6}
                        >

                            <TextField
                                fullWidth
                                multiline
                                minRows={2}
                                label="Remarks"
                                name="remarks"
                                value={formData.remarks}
                                onChange={handleChange}
                            />

                        </Grid>


                        {/* --------------------------------
                                   BUTTONS
                                   -------------------------------- */}

                        <Grid
                            item
                            xs={12}
                        >

                            <div className="vaccination-form-actions">

                                <Button
                                    type="submit"
                                    variant="contained"
                                    startIcon={editingId ? <Edit/> : <Add/>}
                                    disabled={loading}
                                >

                                    {loading ? "Saving..." : editingId ? "Update Appointment" : "Schedule Appointment"}

                                </Button>


                                <Button
                                    type="button"
                                    variant="outlined"
                                    startIcon={<Clear/>}
                                    onClick={resetForm}
                                >
                                    Reset
                                </Button>

                            </div>

                        </Grid>

                    </Grid>

                </form>

            </CardContent>

        </Card>)}


        {/* ====================================================
               APPOINTMENT HISTORY
               ==================================================== */}

        {selectedPatient && (<Card className="vaccination-section-card">

            <CardContent>

                <div className="vaccination-section-title">

                    <CalendarMonth/>

                    <Typography variant="h6">
                        Vaccination Appointments
                    </Typography>

                </div>


                <div className="vaccination-grid-container">

                    {appointmentLoading ? (<div className="vaccination-loading">

                        <CircularProgress/>

                    </div>) : (<DataGrid
                        rows={appointments}
                        columns={columns}
                        getRowId={row => row.id}
                        slots={{
                            toolbar: GridToolbar
                        }}
                        disableRowSelectionOnClick
                        pageSizeOptions={[10, 25, 50]}
                        initialState={{
                            pagination: {
                                paginationModel: {
                                    pageSize: 10
                                }
                            }
                        }}
                    />)}

                </div>

            </CardContent>

        </Card>)}

    </div>);
};
export default VaccinationAppointments;